// React et hooks
import { useEffect, useMemo, useState, useContext, useRef } from "react";

// MUI
import { Box, Paper, Button, Typography, useTheme, Skeleton, Stack, IconButton, Chip } from "@mui/material";
import { DataGridPro, GridColDef, GridColumnVisibilityModel, GridSortModel, useGridApiRef } from '@mui/x-data-grid-pro';

// Redux
import { useDispatch, useSelector } from "react-redux";

// Context et reducers
import { useCurrentWorkspace } from "src/context/reducers/app-settings";

// API queries
import {
    useGetCompetitionEvolutionKpisQuery,
    useGetVisibilityKpisQuery,
    useGetCompetitionEvolutionVisibilityCardQuery,
} from "src/context/api/competitorsMonitoring/api";

// Localisation
import { useLocales } from "src/locales";

// Utilitaires
import { fDate } from "src/utils/format-time";
import countriesJson from "src/utils/countries.json";
import { SplashScreen } from 'src/components/loading-screen';
import { useLocation } from 'react-router-dom';
import {
    getLevelColor,
    mapLevelToLabel,
    coverageToLevel,
} from "src/components/visibility/utils";
import { useCountryData } from "src/components/visibility/hooks/useCountryData";

import { PresenceSegments } from "src/components/visibility/types";
import { createDataGridColumns, SEARCH_TERMS_COLUMN_VISIBILITY } from "src/components/visibility/utils/datagrid-config";

// Composants
import PresenceRateChart from 'src/components/visibility/components/PresenceRateChart';
import { CardBar } from 'src/components/visibility/components/CardBar';
import { SearchTermAnalysisHeader } from "src/components/visibility/components/SearchTermAnalysisHeader";
import type { TopCompetitorStat } from 'src/components/visibility/components/TopCompetitorsCard';

// Hooks personnalisés
import { useResponsiveConfig } from "src/components/visibility/utils/use-responsive-config";

// Context centralisé
import { VisibilityContext } from "src/components/visibility/components/VisibilityProvider";

// Styles
import "src/components/visibility/style.css";
import { CountryBar as CountryFilter } from 'src/components/country-bar';
import Iconify from "src/components/iconify";
import { HEADER } from "src/layouts/config-layout";
import { CountryTabLabel } from "../explore/CountryTabLabel";
import { PAGE_PADDING_X } from "../funnel/constants";
import CustomTooltip from "src/components/visibility/components/CustomTooltip";
import GenericInfiniteScrollList from "../generic-infinite-scroll-list";
import { RootState } from "src/context/store";
import { setFilterTags } from "src/context/reducers/filter-settings/filterSlice";
import { generateColorFromString } from "../tag-manager/datagrid/add-tag-cell";
import { toVisibilityTimestep } from "src/utils/visibility-timestep";


export default function SearchTermsAnalysis() {
    const { t } = useLocales();
    const theme = useTheme();
    const location = useLocation();
    const prefilledFromQueryRef = useRef<boolean>(false);

    const dispatch = useDispatch();

    // Configuration responsive granulaire
    const responsiveConfig = useResponsiveConfig();

    // Essayer d'utiliser le contexte centralisé si disponible
    const context = useContext(VisibilityContext);

    // Fallback pour l'utilisation standalone
    const standaloneWorkspace: any = useCurrentWorkspace();
    const standaloneFilterStore = useSelector((state: any) => state.filters);

    // Utiliser le contexte s'il est disponible, sinon les valeurs standalone
    const workspace = context?.workspace || standaloneWorkspace;
    const filterStore = context?.filterStore || standaloneFilterStore;

    // Fetch competitive intelligence data uniquement si pas de contexte ET si on est bien dans le mode standalone
    // Si le contexte existe, la query a déjà été exécutée par le Provider
    const skipBaseQuery = context ? true : (!filterStore.start || !filterStore.end || !workspace?.id);

    // Traitement des pays (contexte ou standalone)
    // Fetch countries data
    const { countriesData, isLoading } = useCountryData();

    const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
    const [isAbsMode, setIsAbsMode] = useState<boolean>(false);

    // Cette query doit toujours s'exécuter car elle fournit les données du tableau
    // Elle ne devrait pas être skippée même si le contexte est présent
    // On skip seulement si les filtres de base ne sont pas disponibles
    const skipStaQuery = isLoading || !filterStore.start || !filterStore.end || !workspace?.id || (!selectedCountry && (!Array.isArray(filterStore.countries) || filterStore.countries.length === 0));

    const { data: competitionEvolutionData, error: competitionEvolutionError, isFetching: isFetchingCompetitionEvolution } = useGetCompetitionEvolutionKpisQuery({
        startDate: filterStore.start !== undefined ? fDate(filterStore.start, "yyyy-MM-dd") : null,
        endDate: filterStore.end !== undefined ? fDate(filterStore.end, "yyyy-MM-dd") : null,
        compareStartDate: filterStore.compareStart !== undefined ? fDate(filterStore.compareStart, "yyyy-MM-dd") : null,
        compareEndDate: filterStore.compareEnd !== undefined ? fDate(filterStore.compareEnd, "yyyy-MM-dd") : null,
        timestep: toVisibilityTimestep(filterStore.timestep),
        devices: filterStore.devices,
        platforms: filterStore.platforms,
        compare: filterStore?.compare,
        tags: (filterStore.tags || [])
            .map((tag: any) => typeof tag === 'object' ? tag.id : tag)
            .filter((id: any) => id !== undefined && id !== null),
        countries: selectedCountry ? [selectedCountry] : filterStore.countries,
        keywords: filterStore.keywords,
        workspaceId: workspace?.id,
    }, {
        skip: skipStaQuery,
    });

    const { data: visibilityCardData, isFetching: isFetchingVisibilityCard } = useGetCompetitionEvolutionVisibilityCardQuery({
        startDate: filterStore.start !== undefined ? fDate(filterStore.start, "yyyy-MM-dd") : null,
        endDate: filterStore.end !== undefined ? fDate(filterStore.end, "yyyy-MM-dd") : null,
        devices: filterStore.devices || [],
        keywords: filterStore.keywords || [],
        platforms: filterStore?.platforms || [],
        workspaceId: workspace?.id,
        selectedCountry,
    }, {
        skip: skipStaQuery,
    });

    const {
        data: cardBarKpisData,
        isFetching: isFetchingCardBarKpis,
    } = useGetVisibilityKpisQuery(
        {
            startDate: filterStore.start !== undefined
                ? fDate(filterStore.start, 'yyyy-MM-dd')
                : null,
            endDate: filterStore.end !== undefined
                ? fDate(filterStore.end, 'yyyy-MM-dd')
                : null,
            compareStartDate: filterStore.compareStart !== undefined
                ? fDate(filterStore.compareStart, 'yyyy-MM-dd')
                : null,
            compareEndDate: filterStore.compareEnd !== undefined
                ? fDate(filterStore.compareEnd, 'yyyy-MM-dd')
                : null,
            timestep: toVisibilityTimestep(filterStore.timestep),
            devices: filterStore.devices,
            compare: filterStore?.compare,
            countries: selectedCountry ? [selectedCountry] : filterStore.countries,
            keywords: filterStore.keywords,
            platforms: filterStore?.platforms,
            workspaceId: workspace?.id,
        },
        {
            skip: skipStaQuery,
        }
    );

    // Les filtres sont déjà initialisés dans le Redux store, pas besoin de les réinitialiser

    const loadedCountries = Array.isArray(filterStore?.loadedCountries) ? filterStore.loadedCountries : [];

    // Initialisation pays: contexte > query param > premier pays (fallback standalone)
    useEffect(() => {
        // 1) Contexte (le plus prioritaire si présent)
        if (context?.selectedCountry != null) {
            setSelectedCountry(context.selectedCountry);
            return;
        }
        // 2) Deep link via ?country=FR (code ISO → nom)
        const params = new URLSearchParams(location.search || (typeof window !== 'undefined' ? window.location.search : ''));
        const codeParam = params.get('country') || params.get('c');
        if (codeParam && countriesData.length > 0) {
            const mapped = countriesJson?.find((c) => String(c.code).toUpperCase() === String(codeParam).toUpperCase())?.name;
            if (mapped && countriesData.some((c) => c.country_name === mapped)) {
                prefilledFromQueryRef.current = true;
                setSelectedCountry(mapped);
                return;
            }
        }
        // 3) Fallback: sélectionner le premier pays disponible
        if (!prefilledFromQueryRef.current && !selectedCountry) {
            const fallbackFromCi = countriesData.length > 0 ? countriesData[0].country_name : null;
            const fallbackFromLoaded = loadedCountries.find((name: string) => typeof name === 'string' && name.trim());
            const fallback = fallbackFromCi ?? fallbackFromLoaded ?? null;
            if (fallback) {
                setSelectedCountry(fallback);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [context?.selectedCountry, location.search, countriesData, loadedCountries, selectedCountry]);

    // Fonction helper pour obtenir la couleur selon le niveau avec le thème actuel
    const getThemeColor = (level?: string) => getLevelColor(level, theme);

    // Controlled sorting with error handling for rapid page switching
    const apiRef: any = useGridApiRef();
    const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'searchTerm', sort: 'desc' }]);
    const [hasError, setHasError] = useState(false);

    // Cleanup apiRef on component unmount to prevent ARIA errors during rapid switching
    // This addresses the useGridRowAriaAttributes error when switching between overview and search-terms
    useEffect(() => {
        return () => {
            // Reset sort model to clean up DataGrid state before unmounting
            if (apiRef.current) {
                try {
                    apiRef.current.setSortModel([]);
                } catch (error) {
                    // Silently ignore cleanup errors to prevent console spam
                }
            }
        };
    }, [apiRef]);

    const toggleTrafficShareSort = () => {
        try {
            // Get current sort state and toggle direction
            const currentModel = (apiRef.current?.getSortModel?.() as GridSortModel | undefined) || sortModel;
            const current = currentModel[0];
            const currentDir = current && current.field === 'searchTerm' ? current.sort : sortModel[0]?.sort;
            const nextDir = currentDir === 'desc' ? 'asc' : 'desc';
            const nextModel: GridSortModel = [{ field: 'searchTerm', sort: nextDir }];

            // Apply new sort model to both apiRef and local state
            apiRef.current?.setSortModel?.(nextModel);
            setSortModel(nextModel);
            setHasError(false);
        } catch (error) {
            // Handle errors gracefully during rapid switching
            console.warn('Error in toggleTrafficShareSort:', error);
            setHasError(true);
        }
    };

    // Controlled rather than `initialState`: the grid is swapped for a Skeleton on
    // every refetch and remounted on country change, which reset a shown Paid /
    // Organic traffic column to hidden.
    const [columnVisibilityModel, setColumnVisibilityModel] =
        useState<GridColumnVisibilityModel>(SEARCH_TERMS_COLUMN_VISIBILITY);

    // Page-max the Ad cost bar scales to. Computed on the payload rather than per
    // cell: the bars are only comparable against a single maximum.
    const trafficMetricsMax = useMemo(() => {
        const items = (competitionEvolutionData as any[] | undefined) ?? [];
        return {
            adCost: items.reduce(
                (acc: number, item: any) => Math.max(acc, typeof item?.ad_cost === 'number' ? item.ad_cost : 0),
                0,
            ),
        };
    }, [competitionEvolutionData]);

    // Create columns for the DataGrid using externalized configuration
    const columns: GridColDef[] = createDataGridColumns({
        responsiveConfig,
        theme,
        t,
        workspace,
        compare: filterStore?.compare,
        sortModel,
        toggleTrafficShareSort,
        PresenceRateChart,
        columnType: 'searchTerm',
        cellMode: 'searchTermAnalysis',
        selectedCountry,
        trafficMetricsMax,
    });

    const rows = (competitionEvolutionData as any[] | undefined)?.map((item: any, index: number) => {
        const competitors = isAbsMode
            ? (Array.isArray(item?.abs_top_5_competitors) ? item.abs_top_5_competitors : [])
            : (Array.isArray(item?.top_5_competitors) ? item.top_5_competitors : []);

        const allCompetitors = competitors;
        const firstCompetitor = allCompetitors[0];
        const otherCompetitors = allCompetitors.slice(1);
        const filteredCompetitors = firstCompetitor ? [firstCompetitor, ...otherCompetitors] : otherCompetitors;

        const topCompetitors = filteredCompetitors
            .map((c: any) => c?.domain_name)
            .filter(Boolean);

        const topCompetitorsPaidRates: Record<string, number> = filteredCompetitors.reduce(
            (acc: Record<string, number>, c: any) => {
                const domain = c?.domain_name;
                const rate = c?.paid_presence_rate;
                if (domain && typeof rate === 'number') {
                    acc[domain] = rate;
                }
                return acc;
            },
            {}
        );

        const firstCompetitorDomain =
            filteredCompetitors.length > 0 && filteredCompetitors[0]?.domain_name
                ? filteredCompetitors[0].domain_name
                : undefined;

        const isNotScrapped = item?.status === 'not scrapped';

        const rawNewCompetitors = isAbsMode
            ? item?.abs_new_competitors
            : item?.new_competitors;

        const newCompetitors = Array.isArray(rawNewCompetitors)
            ? rawNewCompetitors
                .map((competitor: any) => ({
                    domainName: competitor?.domain_name ?? '',
                    paidPresenceRate:
                        typeof competitor?.paid_presence_rate === 'number'
                            ? competitor.paid_presence_rate
                            : 0,
                    rank: competitor?.rn,
                }))
                .filter(
                    (competitor: { domainName: string }) =>
                        competitor.domainName.length > 0
                )
            : [];

        return {
            id: `${item?.country_name ?? 'NA'}__${item?.search_term ?? index}`,
            searchTerm: item?.search_term ?? '',
            country: item?.country_name ?? '',
            ai_overview_coverage: item?.ai_overview_coverage ?? 0,
            aiOverviewCoverageCompare:
                typeof item?.ai_overview_coverage_compare === 'number'
                    ? item.ai_overview_coverage_compare
                    : null,
            trafficShare: item?.traffic_share ?? 0,
            adCost: item?.ad_cost ?? 0,
            brandTraffic: item?.brand_traffic ?? 0,
            paidTraffic: item?.paid_traffic ?? 0,
            organicTraffic: item?.organic_traffic ?? 0,
            // Compare mode only; null when the compare period has no row for the term.
            adCostCompare: item?.ad_cost_compare ?? null,
            brandTrafficCompare: item?.brand_traffic_compare ?? null,
            paidTrafficCompare: item?.paid_traffic_compare ?? null,
            organicTrafficCompare: item?.organic_traffic_compare ?? null,

            competitionEvolution: isNotScrapped
                ? null
                : mapLevelToLabel(
                    (isAbsMode ? item?.abs_level : item?.level) ??
                    coverageToLevel(item?.coverage)
                ),

            levelRaw: isNotScrapped
                ? null
                : ((isAbsMode ? item?.abs_level : item?.level) ??
                    coverageToLevel(item?.coverage)),

            brandDomain: workspace?.url,
            firstCompetitorDomain: isNotScrapped ? undefined : firstCompetitorDomain,
            isDemoBrand: workspace?.name?.includes('Demo'),

            topCompetitors: isNotScrapped ? [] : topCompetitors,
            topCompetitorsPaidRates: isNotScrapped ? {} : topCompetitorsPaidRates,

            paidPresenceRate: isNotScrapped
                ? null
                : (
                    isAbsMode
                        ? (item?.client_abs_top_paid_presence_rate ?? item?.client_paid_presence_rate ?? 0)
                        : (item?.client_top_paid_presence_rate ?? item?.client_paid_presence_rate ?? 0)
                ),

            blendedPresenceRate: isNotScrapped
                ? null
                : (
                    isAbsMode
                        ? (item?.abs_top_blended_presence_rate ?? 0)
                        : (item?.blended_presence_rate ?? 0)
                ),

            blendedPresenceRateCompare: isNotScrapped
                ? null
                : (
                    isAbsMode
                        ? (item?.abs_top_blended_presence_rate_compare ?? null)
                        : (item?.blended_presence_rate_compare ?? null)
                ),

            firstCompetitorPaidPresenceRate: isNotScrapped
                ? null
                : (
                    firstCompetitorDomain
                        ? (
                            competitors.find(
                                (c: any) => c?.domain_name === firstCompetitorDomain
                            )?.paid_presence_rate ?? 0
                        )
                        : 0
                ),

            newCompetitors: isNotScrapped
                ? []
                : newCompetitors,

            presenceRateData: isNotScrapped
                ? {
                    organicOnly: 0,
                    soloBidder: 0,
                    topAdPosition: 0,
                    competitorsOnly: 0,
                }
                : {
                    organicOnly: isAbsMode
                        ? (item?.abs_top_organic_only ?? 0)
                        : (item?.organic_only ?? 0),

                    soloBidder: isAbsMode
                        ? (item?.abs_top_solo_bidder ?? 0)
                        : (item?.solo_bidder ?? 0),

                    topAdPosition: isAbsMode
                        ? (item?.abs_top_ad_position ?? 0)
                        : (item?.top_ad_position ?? 0),

                    competitorsOnly: isAbsMode
                        ? (item?.abs_top_compet_only ?? 0)
                        : (item?.compet_only ?? 0),
                },

            presenceRateDataCompare: isNotScrapped
                ? {
                    organicOnly: 0,
                    soloBidder: 0,
                    topAdPosition: 0,
                    competitorsOnly: 0,
                }
                : {
                    organicOnly: isAbsMode
                        ? (item?.abs_top_organic_only_compare ?? 0)
                        : (item?.organic_only_compare ?? 0),

                    soloBidder: isAbsMode
                        ? (item?.abs_top_solo_bidder_compare ?? 0)
                        : (item?.solo_bidder_compare ?? 0),

                    topAdPosition: isAbsMode
                        ? (item?.abs_top_ad_position_compare ?? 0)
                        : (item?.top_ad_position_compare ?? 0),

                    competitorsOnly: isAbsMode
                        ? (item?.abs_top_compet_only_compare ?? 0)
                        : (item?.compet_only_compare ?? 0),
                },

            status: item?.status,
        };
    }) ?? [];

    const hasNoData = !isFetchingCompetitionEvolution && rows.length === 0;
    // Solo bidder rate card: series from visibility card; main KPI from total_weekly_solo_bidder_rate
    const soloBidderWeeklySeries = useMemo(() => {
        const series = visibilityCardData?.data?.visibility_rate_weekly_series
            ?? visibilityCardData?.data?.blended_presence_rate_weekly_series;
        if (!Array.isArray(series)) return undefined;
        return series.map((d: { week_start?: string; solo_bidder?: number }) => ({
            week_start: d?.week_start ?? '',
            solo_bidder: typeof d?.solo_bidder === 'number' ? d.solo_bidder : 0,
        }));
    }, [visibilityCardData?.data?.visibility_rate_weekly_series, visibilityCardData?.data?.blended_presence_rate_weekly_series]);

    const soloBidderValue = useMemo(() => {
        const total = visibilityCardData?.data?.total_weekly_solo_bidder_rate;
        if (typeof total === 'number' && !Number.isNaN(total)) return total;
        if (!Array.isArray(soloBidderWeeklySeries) || soloBidderWeeklySeries.length === 0) return undefined;
        const last = soloBidderWeeklySeries[soloBidderWeeklySeries.length - 1];
        const v = last?.solo_bidder;
        return typeof v === 'number' && !Number.isNaN(v) ? v : undefined;
    }, [visibilityCardData?.data?.total_weekly_solo_bidder_rate, soloBidderWeeklySeries]);

    // Card top 3 compet: comes from main_overview.sql payload
    const topCompetitorsData = useMemo<TopCompetitorStat[]>(() => {
        const firstItem = (cardBarKpisData as any[] | undefined)?.[0];
        const top3FromBackend =
            firstItem?.top_3_competitors
            ?? firstItem?.top_3_compet
            ?? firstItem?.card_top_3_competitors;
        if (Array.isArray(top3FromBackend) && top3FromBackend.length > 0) {
            return top3FromBackend
                .map((item: any) => ({
                    domain: String(item?.domain_name ?? item?.domain ?? '').trim(),
                    paidPresenceRate: typeof item?.paid_presence_rate === 'number' ? item.paid_presence_rate : 0,
                }))
                .filter((item: TopCompetitorStat) => item.domain.length > 0)
                .sort((a: TopCompetitorStat, b: TopCompetitorStat) => b.paidPresenceRate - a.paidPresenceRate)
                .slice(0, 3);
        }

        const stats = new Map<string, { total: number; count: number }>();
        const workspaceDomains = new Set<string>();
        if (workspace?.url) {
            workspaceDomains.add(workspace.url.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, ''));
        }
        if (workspace?.friendlyDomains && Array.isArray(workspace.friendlyDomains)) {
            workspace.friendlyDomains.forEach((d: string) => {
                workspaceDomains.add(d.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, ''));
            });
        }
        (cardBarKpisData as any[] | undefined)?.forEach((item: any) => {
            if (!Array.isArray(item?.top_5_competitors)) return;
            item.top_5_competitors.forEach((competitor: any) => {
                const domain = competitor?.domain_name;
                const rate = competitor?.paid_presence_rate;
                if (!domain || typeof rate !== 'number' || rate <= 0) return;
                const normalizedDomain = domain.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '');
                if (workspaceDomains.has(normalizedDomain)) return;
                const current = stats.get(domain) ?? { total: 0, count: 0 };
                current.total += rate;
                current.count += 1;
                stats.set(domain, current);
            });
        });
        return Array.from(stats.entries())
            .map(([domain, { total, count }]) => ({
                domain: domain.trim(),
                paidPresenceRate: count ? Math.round((total / count) * 10) / 10 : 0,
            }))
            .filter((item: TopCompetitorStat) => item.domain.length > 0)
            .sort((a, b) => b.paidPresenceRate - a.paidPresenceRate)
            .slice(0, 3);
    }, [cardBarKpisData, workspace]);

    // Card visibility/solo bidder: comes from cest_card_visibility_rate.sql
    const blendedPresenceRateWeeklySeries = useMemo(() => {
        const series = visibilityCardData?.data?.visibility_rate_weekly_series;
        if (Array.isArray(series)) {
            return series;
        }
        return undefined;
    }, [visibilityCardData?.data?.visibility_rate_weekly_series]);

    // Top metric for visibility card from dedicated SQL
    const blendedPresenceTopMetric = useMemo(() => {
        const visibilityTotal = Number(visibilityCardData?.data?.visibility_rate_total ?? 0);
        if (visibilityTotal > 0) {
            return `${Math.round(visibilityTotal)}%`;
        }

        const validRates = rows
            .map((row) => row.blendedPresenceRate)
            .filter((rate): rate is number => typeof rate === 'number' && rate > Math.round(0));
        if (validRates.length > 0) {
            const averageRate = validRates.reduce((sum: number, rate: number) => sum + rate, 0) / validRates.length;
            return `${Math.round(averageRate)}%`;
        }
        return undefined;
    }, [visibilityCardData?.data?.visibility_rate_total, rows]);

    if (!filterStore?.countries || !filterStore?.keywords || !filterStore?.devices) {
        return <SplashScreen />;
    }

    const gridHeight = Math.min(rows.length, 10) * 44 + 116;

    const CustomToolbar = (props: any) => {
        return (
            <Stack
                display={"flex"}
                flexDirection={"row"}
                justifyContent={"space-between"}
                direction="row"
                alignItems="center"
                sx={{
                    display: 'flex',
                    height: '40px',
                    justifyContent: 'space-between',
                    backgroundColor: 'white',
                    borderTopRightRadius: "8px",
                    flexShrink: 0,
                    borderTopLeftRadius: "8px",
                    borderBottom: "1px dashed rgba(145, 158, 171, 0.20)",
                }}
            >

                <Stack direction="row" alignItems="center" px={1} spacing={0.5}>
                    <CustomTooltip title={"Show More KPIs"} arrow placement="top">
                        <IconButton
                            onClick={() => apiRef.current?.showPreferences("columns")}
                            sx={{
                                width: 26,
                                height: 26,
                                padding: 0,
                                color: '#919EAB',
                                '&:hover': {
                                    backgroundColor: 'action.hover',
                                }
                            }}
                        >
                            <Iconify icon="mingcute:calendar-week-line" width={18} height={18} />
                        </IconButton>
                    </CustomTooltip>

                    <CustomTooltip title={"Apply Filters"} arrow placement="top">
                        <IconButton
                            onClick={() => apiRef.current?.showFilterPanel()}
                            sx={{
                                width: 26,
                                height: 26,
                                padding: 0,
                                color: '#919EAB',
                                '&:hover': {
                                    backgroundColor: 'action.hover',
                                }
                            }}
                        >
                            <Iconify icon="ic:round-filter-list" width={18} height={18} />
                        </IconButton>
                    </CustomTooltip>

                    <CustomTooltip title={"Download Table"} arrow placement="top">
                        <IconButton
                            onClick={() => apiRef.current?.exportDataAsCsv({ fileName: 'search_terms', utf8WithBom: true })}
                            sx={{
                                width: 24,
                                height: 24,
                                padding: 0,
                                color: '#919EAB',
                                '&:hover': {
                                    backgroundColor: 'action.hover',
                                }
                            }}
                        >
                            <Iconify icon="lets-icons:export" width={16} height={16} />
                        </IconButton>
                    </CustomTooltip>

                </Stack>

                <Box
                    sx={{
                        ml: 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        height: '100%',
                        pr: '20px',
                        flexShrink: 0,
                    }}
                >
                    <GenericInfiniteScrollList
                        limitTags={2}
                        sx={{
                            width: '200px',
                            flexShrink: 0,

                            '& .MuiAutocomplete-inputRoot > .MuiChip-root + .MuiChip-root': {
                                marginLeft: '4px !important',
                            },

                            '& .MuiAutocomplete-root': {
                                height: '32px',
                            },

                            '& .MuiInputBase-root': {
                                height: '32px',
                                minHeight: '32px',
                                alignItems: 'center',
                            },

                            '& .MuiAutocomplete-inputRoot': {
                                height: '32px',
                                minHeight: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                flexWrap: 'nowrap',
                                padding: '3px 8px !important',
                            },

                            '& .MuiChip-root': {
                                height: '24px',
                            },
                        }}
                        fullWidth
                        searchPlaceholder=""
                        items={(filterStore as RootState['filters']).loadedTags}
                        options={(filterStore as RootState['filters']).loadedTags}
                        multiple
                        value={
                            filterStore.tags.length === 0
                                ? [{ id: '__default__', label: 'All Clusters' }]
                                : filterStore.tags
                        }
                        onSelectionChange={(selected) => {
                            const cleaned = selected.filter(
                                (item: any) => item.id !== '__default__'
                            );

                            dispatch(
                                setFilterTags(
                                    cleaned.map((selectedTag: any) =>
                                        filterStore.loadedTags.find(
                                            (tag: any) =>
                                                tag.id === (selectedTag.id || selectedTag)
                                        )
                                    )
                                )
                            );
                        }}
                        getOptionLabel={(item: any) => item?.label || item}
                        getOptionKey={(item: any) => item.id || item}
                        isOptionEqualToValue={(item, value) =>
                            (item.id || item) === (value.id || value)
                        }
                        customItemRenderer={(item: any) => (
                            <Chip
                                label={item.label || item}
                                size="small"
                                variant="soft"
                                sx={{
                                    backgroundColor: item?.label
                                        ? generateColorFromString(String(item.label))
                                        : undefined,
                                    color: '#fff',
                                }}
                            />
                        )}
                        customTagRenderer={(item) => {
                            const isDefault = item.id === '__default__';

                            return (
                                <Chip
                                    label={item.label}
                                    size="small"
                                    variant="soft"
                                    sx={{

                                        height: '24px',
                                        fontSize: '13px',
                                        backgroundColor: 'rgba(145, 158, 171, 0.12)',
                                        color: '#333C47',
                                    }}
                                    onDelete={
                                        isDefault
                                            ? undefined
                                            : () => {
                                                dispatch(
                                                    setFilterTags(
                                                        filterStore.tags.filter(
                                                            (tag: any) => tag.id !== item.id
                                                        ) as any
                                                    )
                                                );
                                            }
                                    }
                                />
                            );
                        }}
                    />

                    <SearchTermAnalysisHeader
                        isAbsMode={isAbsMode}
                        onToggleAbsMode={() => setIsAbsMode((value) => !value)}
                    />
                </Box>
            </Stack>
        )
    }

    const cardBarLoading =
        isFetchingCompetitionEvolution ||
        isFetchingVisibilityCard ||
        isFetchingCardBarKpis ||
        isLoading ||
        competitionEvolutionData === undefined;

    return (
        <Box>
            <CountryFilter
                loading={isLoading}
                countries={countriesData}
                selectedCountry={selectedCountry}
                onCountryChange={(c) => {
                    setSelectedCountry(c);
                    context?.setSelectedCountry(c);
                }}
                backgroundColor="transparent"
                indicator="radio"
            />

            <Box px={'24px'} mt={isLoading ? '20px' : (countriesData && countriesData?.length > 1) ? '20px' : '0px'}>
                <CardBar
                    blendedPresenceTitle="Visibility Rate"
                    blendedPresenceTopMetric={blendedPresenceTopMetric}
                    blendedPresenceWeeklySeries={blendedPresenceRateWeeklySeries}
                    soloBidderTitle="Solo bidder rate"
                    soloBidderValue={soloBidderValue}
                    soloBidderWeeklySeries={soloBidderWeeklySeries}
                    topCompetitorsTitle="Top Competitors"
                    topCompetitorsData={topCompetitorsData}
                    dateRange="12 weeks"
                    loading={cardBarLoading}
                    selectedCountry={selectedCountry}
                    useNonBrandLayout
                />
                <Paper sx={{
                    p: 0,
                    overflow: 'hidden',
                    mt: 2,
                    // Enhanced border visibility for light mode - englobes both filter and DataGrid - lighter
                    border: `1px solid ${theme.palette.mode === 'dark' ? '#919eab33' : '#f0f0f0'}`,
                    borderRadius: 1,
                }}>

                    <Box
                        sx={{
                            zIndex: -100,
                            width: '100%',
                            // No border needed here - parent Paper already has the border
                            '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': {
                                outline: 'none !important',
                            },
                            '& .MuiDataGrid-cell': {
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                // Override des bordures dashed par des bordures solides - lighter
                                borderBottom: `1px solid ${theme.palette.mode === 'dark' ? '#919eab33' : '#f0f0f0'} !important`,
                            },
                            '& .row-not-scrapped .MuiDataGrid-cell': {
                                color: theme.palette.text.disabled,
                            },
                            '& .leftAlignCell': {
                                justifyContent: 'flex-start !important',
                            },
                            '& .leftAlignCell .MuiDataGrid-cellContent': {
                                justifyContent: 'flex-start !important',
                                textAlign: 'left',
                                width: 'auto',
                                marginRight: 'auto',
                            },
                            '& .MuiDataGrid-row': {
                                alignItems: 'center',
                            },
                            '& .MuiDataGrid-columnHeader': {
                                display: 'flex',
                                alignItems: 'center',
                                // Enhanced header border visibility - lighter
                                borderBottom: `1px solid ${theme.palette.mode === 'dark' ? '#919eab33' : '#f0f0f0'} !important`,
                            },
                            '& .MuiDataGrid-columnHeader--alignLeft .MuiDataGrid-columnHeaderTitleContainer': {
                                justifyContent: 'flex-start',
                            },
                            '& .MuiDataGrid-cellContent': {
                                display: 'flex',
                                alignItems: 'center',
                                height: '100%',
                                width: '100%',
                            },
                            '& .MuiDataGrid-columnHeaderTitleContainer': {
                                width: '100%',
                                flex: 1,
                                padding: 0,
                            },

                            '& .MuiDataGrid-columnHeaderTitleContainerContent': {
                                width: '100%',
                                flex: 1,
                                color: '#637381!important'
                            },
                            '& .MuiDataGrid-cell[data-field="competitionEvolution"]': {
                                justifyContent: 'flex-start !important',
                                textAlign: 'left',
                            },
                            '& .MuiDataGrid-cell[data-field="competitionEvolution"] > .MuiDataGrid-cellContent': {
                                justifyContent: 'flex-start !important',
                                textAlign: 'left !important',
                                width: 'auto',
                                marginRight: 'auto',
                            },
                            '& .MuiDataGrid-cell.MuiDataGrid-cell--textLeft[data-field="competitionEvolution"]': {
                                justifyContent: 'flex-start !important',
                            },
                            // Masquer la flèche de tri native sur la colonne searchTerm
                            '& .MuiDataGrid-columnHeader[data-field="searchTerm"] .MuiDataGrid-sortIcon': {
                                display: 'none !important',
                            },
                            // Ensure header title container spans full width to right-align custom (icon + arrow)
                            '& .MuiDataGrid-columnHeader.searchTermHeader .MuiDataGrid-columnHeaderTitleContainer': {
                                width: '100%',
                                flex: '1 1 auto',
                                overflow: 'visible',
                                paddingRight: 0,
                                minWidth: 0,
                                marginRight: '0 !important',
                                display: 'flex !important',
                                alignItems: 'center',
                                justifyContent: 'space-between !important',
                            },
                            '& .MuiDataGrid-columnHeader.searchTermHeader .MuiDataGrid-columnHeaderTitleContainerContent': {
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            },
                            '& .MuiDataGrid-columnHeader.searchTermHeader .MuiDataGrid-columnHeaderTitle': {
                                display: 'flex',
                                alignItems: 'center',
                                flex: '1 1 auto',
                                minWidth: 0,
                                marginRight: '8px',
                            },
                            '& .MuiDataGrid-columnHeader.searchTermHeader .MuiDataGrid-columnHeaderDraggableContainer': {
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                            },
                            '& .MuiDataGrid-columnHeader.searchTermHeader': {
                                paddingRight: '8px',
                            },
                            '& .MuiDataGrid-columnHeader.searchTermHeader .MuiDataGrid-menuIconButton': {
                                display: 'none !important',
                            },
                            '& .MuiDataGrid-columnHeader.searchTermHeader .MuiDataGrid-iconSeparator': {
                                display: 'inline-flex',
                            },
                            // Suppression de la bordure du toolbar container
                            '& .MuiDataGrid-toolbarContainer': {
                                borderBottom: 'none !important',
                            },
                            "& .MuiTablePagination-toolbar": {
                                minHeight: 40,
                                height: 40,
                                color: "#919EAB",
                                px: "24px"
                            },

                            "& .MuiDataGrid-footerContainer": {
                                minHeight: "40px !important",
                                height: "40px !important",
                                backgroundColor: "#FAFAFA",
                            },

                            "& .MuiTablePagination-selectLabel, \
                            & .MuiTablePagination-displayedRows": {
                                color: "#919EAB",
                            },

                            "& .MuiTablePagination-selectIcon, \
                            & .MuiTablePagination-actions button": {
                                color: "#919EAB",
                            },
                            "& .MuiTablePagination-actions .MuiIconButton-root.Mui-disabled": {
                                color: "#919EAB",
                                opacity: 0.48,
                            },
                        }}
                    >
                        {isFetchingCompetitionEvolution || isLoading ? <Skeleton variant="rectangular" height={556} /> : hasError ? (
                            // Error fallback UI for DataGrid rendering issues during rapid switching
                            <Box sx={{ p: 3, textAlign: 'center' }}>
                                <Typography variant="h6" color="error" gutterBottom>
                                    {t('dashboard.visibility.dataGridError.title')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    {t('dashboard.visibility.dataGridError.message')}
                                </Typography>
                                <Button
                                    variant="outlined"
                                    onClick={() => setHasError(false)}
                                >
                                    {t('dashboard.visibility.dataGridError.retry')}
                                </Button>
                            </Box>
                        ) : hasNoData ? (
                            <Box sx={{ p: 3, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary">
                                    {t('no_data_available')}
                                </Typography>
                            </Box>
                        ) : (
                            <Box sx={{
                                "& .MuiDataGrid-columnHeader": {
                                    backgroundColor: 'rgba(226, 229, 233, 0.16)!important'
                                },
                                "& .MuiDataGrid-columnHeaderTitleContainerContent": {
                                    color: 'rgba(99, 115, 129, 1)!important'
                                },
                                "& .MuiDataGrid-cell": {
                                    border: "1px solid rgba(145, 158, 171, 0.20)"
                                },
                                height: gridHeight,
                            }}>
                                <DataGridPro
                                    // Unique key forces complete remount on workspace/country change, preventing ARIA conflicts
                                    key={`search-term-datagrid-${workspace?.id || 'default'}-${selectedCountry || 'all'}`}
                                    apiRef={apiRef}
                                    autoHeight={false}
                                    disableDensitySelector
                                    checkboxSelection={false}
                                    disableRowSelectionOnClick
                                    showCellVerticalBorder
                                    rows={rows}
                                    columnHeaderHeight={36}
                                    rowHeight={44}
                                    columns={columns}
                                    getRowClassName={(params) => (params.row?.status === 'not scrapped' ? 'row-not-scrapped' : '')}
                                    sortingMode="client"
                                    disableMultipleColumnsSorting
                                    sortingOrder={['desc', 'asc']}
                                    sortModel={sortModel}
                                    onSortModelChange={(newModel) => {
                                        try {
                                            // Update local sort state, handled by DataGrid internally
                                            setSortModel(newModel);
                                        } catch (error) {
                                            // Handle sort model errors during rapid switching
                                            console.warn('Error in onSortModelChange:', error);
                                        }
                                    }}
                                    pagination
                                    pageSizeOptions={[10, 20, 50, 100]}
                                    initialState={{
                                        pagination: {
                                            paginationModel: { pageSize: 10, page: 0 },
                                        },
                                    }}
                                    columnVisibilityModel={columnVisibilityModel}
                                    onColumnVisibilityModelChange={setColumnVisibilityModel}
                                    slots={{ toolbar: CustomToolbar }}
                                    slotProps={{
                                        toolbar: {
                                            printOptions: { disableToolbarButton: true },
                                            csvOptions: { disableToolbarButton: false }
                                        },
                                    }}
                                />
                            </Box>
                        )}
                    </Box>
                </Paper>
            </Box>
        </Box>
    );
}
